import { createSharedComposable } from '@vueuse/core';
import { computed, readonly, ref, watch } from 'vue';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { MaterialFormulationApi } from '@/domains/material/apis/formulation-api';
import type {
  AgentMessageResponse,
  FormulationMessage,
  AiRefineRequest,
  FormulationItemWithLock,
  AllSearchSourceItem,
} from '@/types/formulation/formulation';
import { useRouter } from 'vue-router';
import { useLoginSdk } from '../use-loginsdk';
import { i18n } from '@/infrastructure/locales';
import { useLimitReach } from '../utils/use-limit-reach';
import { useFormulationTracking } from './use-formulation-tracking';
import type { SearchJsonDocItem } from './types';
import {
  MESSAGE_STATUS,
  SEARCH_TYPE_KEYS,
  STREAMING_MESSAGE_ID,
  STREAMING_TOOL_DISPLAY_ID,
  SKIP_AUTO_OPEN_TOOL_KEYS,
  EUREKA_REPORT_TOOL_KEY,
  TASK_STATUS,
} from './types';
import { splitEurekaReport } from './eureka-report';
import { mapRawToSearchDocs, transformAgentMessageToFormulationMessage } from './mappers';
import {
  mergeConsecutiveSameToolKeyMessages,
  mergeConsecutiveSearchMessages,
} from './message-utils';
import { createGetConversation } from './conversation-loader';
import { createStreamConversation } from './stream/stream-conversation';
import { getExamples } from '../home/use-examples';
import { useTracking } from '../tracking/use-tracking';

const { getProductExample } = getExamples();

const { openLoginDialog } = useLoginSdk(i18n as any);

// 重新导出供外部使用
export { STREAMING_MESSAGE_ID, STREAMING_TOOL_DISPLAY_ID } from './types';
export { mapRawToSearchDocs } from './mappers';
export type { SearchJsonDocItem } from './types';

type DetailViewOrigin = 'user_click' | 'auto_open';

// 模块级别的 contextId，确保在整个应用中共享
const moduleContextId = ref<number>();

export const useFormulationConversation = createSharedComposable(() => {
  const isLoadingConversation = ref(false);
  const conversationData = ref<AgentMessageResponse[]>();
  const materialFormulationApiService =
    injectService<MaterialFormulationApi>(MaterialFormulationApi);
  const selectedToolKey = ref<string>('');
  const pipelineRunning = ref(false);
  const taskStatus = ref('');
  const isCreatingConversation = ref(false);
  // 存储 agent message 结果，key 为 sessionId-agentMessageId
  const toolResults = ref<Record<string, AgentMessageResponse>>({});
  // 用于跟踪刚创建的 session，避免首次导航时重复拉取消息
  const justCreatedSessionId = ref<string>('');
  const createdSessionId = ref('');
  // 存储搜索文档列表，用于 search_summary 的引用 hover
  const searchDocsList = ref<SearchJsonDocItem[]>([]);
  // Cached all-search-sources results grouped by iteration for the current session.
  const sourcesByIteration = ref<Record<number, AllSearchSourceItem[]>>({});
  // Session id that owns sourcesByIteration, used to ignore stale cache after navigation.
  const sourcesByIterationSessionId = ref('');
  // Iteration currently opened in the right-side sources panel.
  const selectedSourcesIteration = ref<number>();
  // 存储 search_log 日志内容，用于在工具卡片中展示
  const searchLogs = ref<string[]>([]);
  /** AI Refine 开关：为 true 时右侧配方表可编辑，左侧输入框上方显示说明浮层 */
  const aiRefineEnabled = ref(false);
  /** 当前配方表中已 AI 锁定的行（用于左侧输入框上方展示「您已经锁定 成分 浓度」） */
  const formulationLockedRowsSummary = ref<{ component: string; concentration: string }[]>([]);
  /** 获取带 is_locked 的 formulation 数据的函数（由 FormulateSummary 设置） */
  const getFormulationWithLockStatusFn = ref<(() => FormulationItemWithLock[]) | null>(null);
  /** 用于通知 UI 需要滚动到底部（用户消息添加后触发） */
  const shouldScrollToBottom = ref(false);
  /** 提交前乐观展示的用户输入（在服务端 user 消息写入前先显示在 displayBlock 中） */
  const pendingUserMessage = ref<{ content: string; iteration: number } | null>(null);
  /** 全屏模式：为 true 时左侧 ChatSection 收起，DetailSection 占满内容区 */
  const isFullScreenFocusMode = ref(false);
  /** 当前会话的任务标题（由 getConversation 获取后共享，避免 Header 重复请求） */
  const userTaskTitle = ref('');
  const { sendTracking } = useTracking();

  const router = useRouter();
  const { ifLimitReachDialog } = useLimitReach();
  const { trackFormulation } = useFormulationTracking();

  const sessionId = ref('');
  // 使用模块级别的 contextId
  const contextId = moduleContextId;

  const messages = computed<FormulationMessage[]>(() => {
    if (!conversationData.value) return [];
    // 过滤掉 search_log 类型的消息以及 start_flag/stop_flag，不在左侧显示
    const filtered = conversationData.value.filter(
      (m) =>
        m.tool_key !== 'search_log' &&
        m.business_type !== 'start_flag' &&
        m.business_type !== 'stop_flag',
    );
    const list = filtered.map(transformAgentMessageToFormulationMessage);
    // 相邻且 tool_key 相同的 user_message 合并为一条展示
    const sameKeyMerged = mergeConsecutiveSameToolKeyMessages(list);
    return mergeConsecutiveSearchMessages(sameKeyMerged);
  });

  const currentIteration = computed(() => {
    if (!messages.value.length) return 0;
    return Math.max(...messages.value.map((m) => m.iteration));
  });

  const setPendingUserMessage = (content: string) => {
    pendingUserMessage.value = {
      id: Date.now(),
      role: 'user',
      message: content,
      iteration: Math.max(1, (currentIteration.value || 0) + 1),
      status: MESSAGE_STATUS.COMPLETED,
      timestamp: Date.now(),
    };
  };

  const clearPendingUserMessage = () => {
    pendingUserMessage.value = null;
  };

  const trackLandingExampleClick = (text: string, index?: number) => {
    if (index === undefined) {
      return;
    }

    const exampleIndex = index + 1;
    trackFormulation('mse_formulation_click_example_prompt', {
      example_index: exampleIndex,
    });
  };

  const handleExampleClick = (text: string, index?: number) => {
    trackLandingExampleClick(text, index);
  };

  /** 供展示用：messages + 若有待发送则追加一条 user 消息，用于在 submit 前即显示用户输入气泡 */
  const messagesForDisplay = computed<FormulationMessage[]>(() => {
    const list = messages.value;
    const pending = pendingUserMessage.value;
    if (!pending) return list;

    return [...list, pending];
  });

  const isCompleted = computed(() => {
    if (!messages.value.length) return false;
    const lastMessage = messages.value[messages.value.length - 1];
    return lastMessage.status === MESSAGE_STATUS.COMPLETED && lastMessage.role === 'ai';
  });

  // 存储选中的 agent message ID
  const selectedAgentMessageId = ref<number>(0);
  const selectionOrigin = ref<DetailViewOrigin>('user_click');

  const setSelectedTool = (
    toolKey: string,
    agentMessageId: number,
    origin: DetailViewOrigin = 'user_click',
  ) => {
    selectionOrigin.value = origin;
    selectedToolKey.value = toolKey;
    selectedAgentMessageId.value = agentMessageId;
  };

  /** Eureka report carried by a given AI-text message, derived from its raw content. */
  const getEurekaReportForMessage = (m: AgentMessageResponse | undefined) => {
    if (!m || typeof m.content?.raw !== 'string') return null;
    const split = splitEurekaReport(m.content.raw);
    if (!split.hasReport || !split.report) return null;
    return { messageId: m.id, iteration: m.iteration, report: split.report, generating: split.generating };
  };

  /** Open the eureka report panel for a specific report-bearing message. */
  const openEurekaReport = (
    agentMessageId: number,
    origin: DetailViewOrigin = 'user_click',
  ) => {
    setSelectedTool(EUREKA_REPORT_TOOL_KEY, agentMessageId, origin);
  };

  /** All report-bearing messages in the conversation, in order. */
  const eurekaReports = computed(() =>
    (conversationData.value ?? [])
      .map(getEurekaReportForMessage)
      .filter((r): r is NonNullable<typeof r> => r !== null),
  );

  /**
   * Auto-open the latest report. Mirrors updateStreamingToolDisplay: skip when this
   * report is already the active selection, so it won't re-fire on every chunk.
   */
  watch(
    () => eurekaReports.value[eurekaReports.value.length - 1],
    (latest) => {
      if (!latest) return;
      const alreadyShown =
        selectedToolKey.value === EUREKA_REPORT_TOOL_KEY &&
        selectedAgentMessageId.value === latest.messageId;
      if (alreadyShown) return;
      openEurekaReport(latest.messageId, 'auto_open');
    },
  );

  // 存储 extract 组合面板的 message ID（含 search_summary）
  const extractGroupMessageIds = ref<{
    tableId?: number;
    summaryId?: number;
    searchSummaryId?: number;
  }>({});

  /* eslint-disable @typescript-eslint/no-use-before-define */
  const setSelectedExtractGroup = (
    tableId: number | undefined,
    summaryId: number | undefined,
    origin: DetailViewOrigin = 'user_click',
  ) => {
    selectionOrigin.value = origin;
    selectedToolKey.value = 'extract_report';
    const primaryId = tableId ?? summaryId ?? 0;
    selectedAgentMessageId.value = primaryId;
    const ids: typeof extractGroupMessageIds.value = { tableId, summaryId };

    // primaryId 由 DetailSection 的 watch 自动获取，这里只获取其他的
    if (tableId && tableId !== primaryId) fetchToolResult(tableId);
    if (summaryId && summaryId !== primaryId) fetchToolResult(summaryId);

    // Auto-find search_summary from conversationData (search 类消息为 user_message 类型)
    const searchSummaryMsg = [...(conversationData.value ?? [])]
      .reverse()
      .find((m) => m.tool_key === 'search_summary' && !!m.id);
    if (searchSummaryMsg?.id) {
      ids.searchSummaryId = searchSummaryMsg.id;
      fetchToolResult(searchSummaryMsg.id);
    }
    extractGroupMessageIds.value = ids;
  };
  /* eslint-enable @typescript-eslint/no-use-before-define */

  const clearSelectedTool = () => {
    selectedToolKey.value = '';
    selectedAgentMessageId.value = 0;
    selectedSourcesIteration.value = undefined;
    extractGroupMessageIds.value = {};
  };

  const scrollToLastMessage = () => {
    requestAnimationFrame(() => {
      const panel = document.querySelector('.left-panel');
      if (panel) {
        panel.scrollTop = panel.scrollHeight;
      }
    });
  };

  /** 进入全屏模式 */
  const enterFullScreenMode = () => {
    isFullScreenFocusMode.value = true;
  };

  /** 退出全屏模式 */
  const exitFullScreenMode = () => {
    isFullScreenFocusMode.value = false;
  };

  const getToolResultKey = (sessionIdValue: string, agentMessageId: number) => {
    return `${sessionIdValue}-${agentMessageId}`;
  };

  const sourcesForSelectedIteration = computed(() => {
    if (
      !selectedSourcesIteration.value ||
      sourcesByIterationSessionId.value !== sessionId.value
    ) {
      return [];
    }
    return sourcesByIteration.value[selectedSourcesIteration.value] ?? [];
  });

  const getSourcesByDocIdForIteration = (iteration: number | undefined) => {
    const map = new Map<string, AllSearchSourceItem>();
    if (!iteration || sourcesByIterationSessionId.value !== sessionId.value) return map;
    for (const source of sourcesByIteration.value[iteration] ?? []) {
      if (source.doc_id) map.set(source.doc_id, source);
    }
    return map;
  };

  const getIterationSourceCount = (iteration: number | undefined) => {
    if (!iteration || sourcesByIterationSessionId.value !== sessionId.value) return 0;
    return sourcesByIteration.value[iteration]?.length ?? 0;
  };

  /** 清除指定 agentMessageId 的缓存，用于强制刷新 */
  const clearToolResultCache = (agentMessageId: number) => {
    if (!sessionId.value) return;
    const key = getToolResultKey(sessionId.value, agentMessageId);
    if (toolResults.value[key]) {
      const newResults = { ...toolResults.value };
      delete newResults[key];
      toolResults.value = newResults;
    }
  };

  const getSelectionIteration = (toolKey: string, agentMessageId: number) => {
    const list = conversationData.value;
    if (agentMessageId === STREAMING_TOOL_DISPLAY_ID) {
      return list?.find((m) => m.id === STREAMING_TOOL_DISPLAY_ID && m.tool_key === toolKey)
        ?.iteration;
    }
    return list?.find((m) => m.id === agentMessageId)?.iteration;
  };

  watch(
    [selectedToolKey, selectedAgentMessageId],
    ([toolKey, agentMessageId], [previousToolKey, previousAgentMessageId]) => {
      if (selectionOrigin.value !== 'user_click') return;
      if (!toolKey || !sessionId.value) return;
      const iteration = getSelectionIteration(toolKey, agentMessageId);
      if (!iteration) return;
      if (previousToolKey === toolKey) {
        if (previousAgentMessageId === agentMessageId) return;
        const previousIteration = getSelectionIteration(previousToolKey, previousAgentMessageId);
        if (
          previousIteration === iteration &&
          (previousAgentMessageId <= 0 || agentMessageId <= 0)
        ) {
          return;
        }
      }
      trackFormulation('mse_formulation_view_detail', {
        session_id: sessionId.value,
        iteration,
        tool_key: toolKey,
        origin: selectionOrigin.value,
        ...(agentMessageId > 0 && { agent_message_id: agentMessageId }),
      });
    },
  );

  /** 若 id 为「搜索」组代表 id（首条且为 search_ai_thinking），返回合并结果；否则返回 null，用单条 tool 结果展示 */
  const getMergedSearchGroupResult = (agentMessageId: number): AgentMessageResponse | null => {
    const list = conversationData.value ?? [];
    const idx = list.findIndex((m) => m.id === agentMessageId);
    if (idx < 0) return null;
    const first = list[idx];
    if (first.business_type !== 'user_message' || !SEARCH_TYPE_KEYS.has(first.tool_key ?? '')) {
      return null;
    }
    // 仅当选中的是 search_ai_thinking 且 run 有多条时才返回合并结果；search_json_docs/search_summary 用单条展示
    if (first.tool_key !== 'search_ai_thinking') return null;
    const run: AgentMessageResponse[] = [first];
    for (let i = idx + 1; i < list.length; i++) {
      const m = list[i];
      if (
        m.business_type === 'user_message' &&
        SEARCH_TYPE_KEYS.has(m.tool_key ?? '') &&
        m.iteration === first.iteration
      ) {
        run.push(m);
      } else {
        break;
      }
    }
    if (run.length <= 1) return null;
    const mergedRaw = run
      .map((r) => (typeof r.content?.raw === 'string' ? r.content.raw : ''))
      .filter(Boolean)
      .join('\n\n');
    return { ...first, content: { raw: mergedRaw } };
  };

  // 用于追踪正在进行中的请求，避免重复发起
  const pendingToolResultRequests = new Map<string, Promise<AgentMessageResponse | null>>();
  const pendingAllSearchSourcesRequests = new Map<string, Promise<AllSearchSourceItem[]>>();

  const fetchToolResult = async (agentMessageId: number) => {
    if (!sessionId.value) return null;
    // 流式消息不请求接口，由 selectedToolResult 从 conversationData 读取
    if (agentMessageId === STREAMING_MESSAGE_ID) {
      return null;
    }

    const key = getToolResultKey(sessionId.value, agentMessageId);

    // 已有缓存结果，直接返回
    if (toolResults.value[key]) {
      return toolResults.value[key];
    }

    // 已有正在进行的请求，复用该 Promise
    if (pendingToolResultRequests.has(key)) {
      return pendingToolResultRequests.get(key)!;
    }

    // 发起新请求并追踪
    const requestPromise = (async () => {
      try {
        // 使用 getAgentMessage 接口获取工具执行结果
        const result = await materialFormulationApiService.getAgentMessage(
          agentMessageId,
          sessionId.value,
        );
        toolResults.value[key] = result;
        return result;
      } catch {
        return null;
      } finally {
        pendingToolResultRequests.delete(key);
      }
    })();

    pendingToolResultRequests.set(key, requestPromise);
    return requestPromise;
  };

  const fetchAllSearchSources = async (
    iteration?: number,
    options?: { force?: boolean },
  ): Promise<AllSearchSourceItem[]> => {
    if (!sessionId.value || !iteration) return [];
    const currentSessionId = sessionId.value;
    const key = `${currentSessionId}:${iteration}`;

    if (
      !options?.force &&
      sourcesByIterationSessionId.value === currentSessionId &&
      sourcesByIteration.value[iteration]
    ) {
      return sourcesByIteration.value[iteration];
    }

    const pendingRequest = pendingAllSearchSourcesRequests.get(key);
    if (pendingRequest && !options?.force) {
      return pendingRequest;
    }
    if (pendingRequest && options?.force) {
      await pendingRequest;
    }

    const requestPromise = materialFormulationApiService
      .getAllSearchSources(currentSessionId, iteration)
      .then((sources) => {
        if (sessionId.value === currentSessionId && sources.length > 0) {
          const existingSources =
            sourcesByIterationSessionId.value === currentSessionId ? sourcesByIteration.value : {};
          sourcesByIteration.value = {
            ...existingSources,
            [iteration]: sources,
          };
          sourcesByIterationSessionId.value = currentSessionId;
        }
        return sources;
      })
      .catch(() => [])
      .finally(() => {
        pendingAllSearchSourcesRequests.delete(key);
      });

    pendingAllSearchSourcesRequests.set(key, requestPromise);
    return requestPromise;
  };

  const openSourcesPanel = async (
    iteration?: number,
    origin: DetailViewOrigin = 'user_click',
  ) => {
    selectedSourcesIteration.value = iteration;
    await fetchAllSearchSources(iteration);
    setSelectedTool('sources_panel', 0, origin);
  };

  /** 是否为工具类消息（document 或 tool_display） */
  const isToolAgentMessage = (m: AgentMessageResponse) =>
    m.business_type === 'tool_display' && !!m.tool_key;

  const EXTRACT_GROUP_TOOL_KEYS = new Set(['extract_table_maker_json_docs', 'extract_summary']);

  /**
   * 只要消息列表中有工具调用（tool_display），就自动打开右侧工具结果页并展示最后一条工具结果。
   * 当最后一条工具属于 extract 组时，自动打开组合面板。
   * intent_summary 和 search 相关面板不自动打开。
   */
  const tryAutoOpenLastToolResult = (list: AgentMessageResponse[] | undefined) => {
    if (!list?.length) return;
    const lastTool = [...list].reverse().find(isToolAgentMessage);
    if (!lastTool?.tool_key || !lastTool?.id) return;

    if (EXTRACT_GROUP_TOOL_KEYS.has(lastTool.tool_key)) {
      const tableMsg = [...list]
        .reverse()
        .find((m) => isToolAgentMessage(m) && m.tool_key === 'extract_table_maker_json_docs');
      const summaryMsg = [...list]
        .reverse()
        .find((m) => isToolAgentMessage(m) && m.tool_key === 'extract_summary');
      setSelectedExtractGroup(tableMsg?.id, summaryMsg?.id, 'auto_open');
      return;
    }

    // intent_summary 和 search 相关面板不自动打开
    if (SKIP_AUTO_OPEN_TOOL_KEYS.has(lastTool.tool_key)) return;

    // 只设置选中状态，fetchToolResult 由 DetailSection 的 watch 自动触发
    setSelectedTool(lastTool.tool_key, lastTool.id, 'auto_open');
  };

  const selectedToolResult = computed(() => {
    if (!selectedToolKey.value || selectedAgentMessageId.value === undefined) {
      return null;
    }
    // eureka_report: derive the report content from its carrier ai_thinking message.
    if (selectedToolKey.value === EUREKA_REPORT_TOOL_KEY) {
      const list = conversationData.value ?? [];
      const carrier = list.find((m) => m.id === selectedAgentMessageId.value);
      const found = getEurekaReportForMessage(carrier);
      if (!found) return null;
      return {
        ...(carrier as AgentMessageResponse),
        tool_key: EUREKA_REPORT_TOOL_KEY,
        content: { raw: found.report },
        tool_state: found.generating ? TASK_STATUS.RUNNING : TASK_STATUS.COMPLETED,
      } as AgentMessageResponse;
    }
    // 流式消息 (id=-1)：直接从 conversationData 取，保证右侧面板随流式内容更新
    if (selectedAgentMessageId.value === STREAMING_MESSAGE_ID) {
      const list = conversationData.value ?? [];
      return list.find((m) => m.id === STREAMING_MESSAGE_ID) ?? null;
    }
    // 流式 tool_display (id=-2)：从 conversationData 取，右侧面板实时展示
    if (selectedAgentMessageId.value === STREAMING_TOOL_DISPLAY_ID) {
      const list = conversationData.value ?? [];
      const streamingTool = list.find((m) => m.id === STREAMING_TOOL_DISPLAY_ID) ?? null;
      return streamingTool?.tool_key === selectedToolKey.value ? streamingTool : null;
    }
    if (!sessionId.value) return null;
    const merged = getMergedSearchGroupResult(selectedAgentMessageId.value);
    if (merged) return merged;
    const key = getToolResultKey(sessionId.value, selectedAgentMessageId.value);
    return toolResults.value[key] || null;
  });

  const extractTableResult = computed(() => {
    const id = extractGroupMessageIds.value.tableId;
    if (!id || !sessionId.value) return null;
    return toolResults.value[getToolResultKey(sessionId.value, id)] ?? null;
  });

  const extractSummaryResult = computed(() => {
    const id = extractGroupMessageIds.value.summaryId;
    if (!id || !sessionId.value) return null;
    return toolResults.value[getToolResultKey(sessionId.value, id)] ?? null;
  });

  const searchSummaryResult = computed(() => {
    const id = extractGroupMessageIds.value.searchSummaryId;
    if (!id || !sessionId.value) return null;
    return toolResults.value[getToolResultKey(sessionId.value, id)] ?? null;
  });

  /** 会话中「检索专利文献」对应的 document/tool_display 消息（用于 citation 列表来源） */
  const searchDocsSourceMessage = computed(() => {
    const list = conversationData.value ?? [];
    const doc = [...list]
      .reverse()
      .find(
        (m) =>
          isToolAgentMessage(m) &&
          (m.tool_key === 'search_json_docs' || m.tool_key === 'filtered_json_docs'),
      );
    return doc ?? null;
  });

  /**
   * 搜索报告 citation 使用的文档列表：与「检索专利文献」结果顺序一致。
   * 优先用流式写入的 searchDocsList；否则从当前会话的 search_json_docs/filtered_json_docs 结果解析。
   */
  const citationDocList = computed<SearchJsonDocItem[]>(() => {
    if (searchDocsList.value.length > 0) return searchDocsList.value;
    const msg = searchDocsSourceMessage.value;
    if (!msg || !sessionId.value) return [];
    const key = getToolResultKey(sessionId.value, msg.id);
    const result = toolResults.value[key];
    const raw = result?.content?.raw;
    return mapRawToSearchDocs(raw);
  });

  // 查看搜索报告/配方报告时自动拉取「检索专利文献」结果，保证 citation hover / source tooltip 有数据
  const toolKeysNeedingCitationDocs = new Set([
    'search_summary',
    'extract_report',
    'formulate_summary',
    'formulate_json_docs',
  ]);
  watch(
    () => [selectedToolKey.value, searchDocsSourceMessage.value] as const,
    ([key, source]) => {
      if (!toolKeysNeedingCitationDocs.has(key) || !source?.id || !sessionId.value) return;
      if (searchDocsList.value.length > 0) return; // 流式已写入则无需拉取
      const k = getToolResultKey(sessionId.value, source.id);
      if (toolResults.value[k]) return; // 已拉过
      fetchToolResult(source.id);
    },
    { immediate: true },
  );

  const guestLoginLimitDialog = () => {
    sendTracking('ss_plg_register', 'register_exposure', {
      registrationSource: 'mse_formulation',
    });
    openLoginDialog(
      false,
      true,
      () => {
        sendTracking('ss_plg_register', 'register_close', {
          registrationSource: 'mse_formulation',
        });
      },
      true,
    );
  };

  /** 与 createConversation 一致：ACL 限额弹窗、访客容量满弹登录。返回 true 表示已识别并处理。 */
  const handleFormulationConnectionFailure = (e: unknown): boolean => {
    const cause = (e as any)?.cause ?? e;
    let handled = false;
    if (cause?.data?.error_code === 'errors.com.patsnap.acl_limit_exceed') {
      ifLimitReachDialog.value = true;
      handled = true;
    }
    const code =
      cause?.error_code ?? cause?.data?.numeric_error_code ?? Number(cause?.data?.error_code);
    if (code === 90180009) {
      guestLoginLimitDialog();
      handled = true;
    }
    return handled;
  };

  const startSessionPipeline = async (content: string, context_id?: number) => {
    pipelineRunning.value = true;
    try {
      const request: { user_input: string; context_id?: number } = { user_input: content };
      if (context_id) {
        request.context_id = context_id;
      }
      const response = await materialFormulationApiService.startSession(request);
      sessionId.value = response.session_id;
      // 每次创建新会话时，根据是否传入 context_id 来设置或重置
      contextId.value = context_id ?? undefined;
      // 标记这个 session 是刚创建的，避免首次导航时重复拉取
      justCreatedSessionId.value = response.session_id;
      createdSessionId.value = response.session_id;
      conversationData.value = [];
      return sessionId.value;
    } catch (error) {
      console.error('[Formulation] Failed to start conversation:', error);
      pipelineRunning.value = false;
      throw error;
    }
  };

  const streamConversation = createStreamConversation({
    sessionId,
    contextId,
    conversationData,
    searchLogs,
    currentIteration,
    selectedToolKey,
    pipelineRunning,
    taskStatus,
    searchDocsList,
    shouldScrollToBottom,
    materialFormulationApiService,
    setSelectedTool: (toolKey, agentMessageId) =>
      setSelectedTool(toolKey, agentMessageId, 'auto_open'),
    tryAutoOpenLastToolResult,
    clearPendingUserMessage,
    onSseConnectionError: handleFormulationConnectionFailure,
  });

  const getConversation = createGetConversation({
    sessionId,
    contextId,
    justCreatedSessionId,
    isLoadingConversation,
    pipelineRunning,
    taskStatus,
    conversationData,
    userTaskTitle,
    materialFormulationApiService,
    streamConversation,
    tryAutoOpenLastToolResult,
    router,
  });

  const createConversation = async (content: string, context_id?: number) => {
    isCreatingConversation.value = true;
    // Reset AI refine state on submit
    aiRefineEnabled.value = false;
    formulationLockedRowsSummary.value = [];

    try {
      const newSessionId = await startSessionPipeline(content, context_id);

      if (newSessionId) {
        // contextId 已通过 startSessionPipeline 设置到共享 ref 中，无需放入路由参数
        router.push({
          name: 'FormulationConversation',
          params: { session_id: newSessionId },
        });
      }
      return newSessionId ?? null;
    } catch (e) {
      handleFormulationConnectionFailure(e);
      return null;
    } finally {
      isCreatingConversation.value = false;
    }
  };

  const createConversationFromLanding = async (content: string, context_id?: number) => {
    const formulationExamples = getProductExample('FORMULATION');
    const index = formulationExamples.findIndex((e) => e.text === content);

    const isExamplePrompt = index !== -1;
    const newSessionId = await createConversation(content, context_id);

    if (newSessionId) {
      trackFormulation('mse_formulation_start_from_landing', {
        entry_method: isExamplePrompt ? 'example_prompt' : 'typed',
        ...(isExamplePrompt && content ? { example_index: index + 1 } : {}),
      });
    }

    return newSessionId;
  };

  const continueConversation = async (content: string, aiRefine?: AiRefineRequest) => {
    if (!sessionId.value) {
      console.error('[Formulation] No session to continue');
      return;
    }
    // Reset AI refine state on submit
    aiRefineEnabled.value = false;
    formulationLockedRowsSummary.value = [];

    pipelineRunning.value = true;
    try {
      await streamConversation(sessionId.value, undefined, content, { aiRefine });
    } catch (error) {
      console.error('[Formulation] Failed to continue conversation:', error);
      handleFormulationConnectionFailure(error);
      pipelineRunning.value = false;
    }
  };

  const stopConversation = async (sessionIdParam: string) => {
    try {
      await materialFormulationApiService.stopSession(sessionIdParam);
    } finally {
      if (sessionId.value === sessionIdParam) {
        pipelineRunning.value = false;
      }
    }
  };

  const resetConversation = () => {
    sessionId.value = '';
    conversationData.value = undefined;
    selectedToolKey.value = '';
    selectedAgentMessageId.value = 0;
    extractGroupMessageIds.value = {};
    pipelineRunning.value = false;
    taskStatus.value = '';
    toolResults.value = {};
    sourcesByIteration.value = {};
    sourcesByIterationSessionId.value = '';
    selectedSourcesIteration.value = undefined;
    pendingAllSearchSourcesRequests.clear();
    justCreatedSessionId.value = '';
    createdSessionId.value = '';
    searchDocsList.value = [];
    searchLogs.value = [];
    pendingUserMessage.value = null;
    userTaskTitle.value = '';
    getFormulationWithLockStatusFn.value = null;
  };

  /** 获取最新 + before selectedId 的 formulate_json_docs 消息 ID */
  const selectedFormulateJsonDocsMessage = computed(() => {
    const list = conversationData.value ?? [];
    const selectedId = selectedAgentMessageId.value;

    if (!selectedId || selectedId <= 0) {
      return [...list]
        .reverse()
        .find((m) => m.tool_key === 'formulate_json_docs' && m.business_type === 'tool_display');
    }

    const candidates = list.filter(
      (m) =>
        m.tool_key === 'formulate_json_docs' &&
        m.business_type === 'tool_display' &&
        m.id < selectedId,
    );

    if (candidates.length === 0) {
      return [...list]
        .reverse()
        .find((m) => m.tool_key === 'formulate_json_docs' && m.business_type === 'tool_display');
    }

    return candidates[candidates.length - 1];
  });

  const latestFormulateJsonDocsMessageId = computed(
    () => selectedFormulateJsonDocsMessage.value?.id ?? 0,
  );

  const selectedFormulationTableVersion = computed(() => {
    const version = selectedFormulateJsonDocsMessage.value?.meta?.version;
    return typeof version === 'number' ? String(version) : '1';
  });

  /** 构建 ai_refine 请求参数 */
  const buildAiRefineRequest = (): AiRefineRequest | undefined => {
    const messageId = latestFormulateJsonDocsMessageId.value;
    const getFormulation = getFormulationWithLockStatusFn.value;
    if (!messageId || !getFormulation) return undefined;

    const formulation = getFormulation();
    if (!formulation.length) return undefined;

    return {
      tool_key: 'formulate_json_docs',
      agent_message_id: messageId,
      content: {
        raw: {
          formulation,
        },
      },
    };
  };

  return {
    // State
    conversationData,
    isLoadingConversation,
    isCreatingConversation,
    pipelineRunning,
    taskStatus,
    selectedToolKey,
    selectedAgentMessageId,
    selectedToolResult,
    toolResults,
    sessionId,
    searchDocsList,
    sourcesByIteration,
    sourcesForSelectedIteration,
    selectedSourcesIteration,
    searchLogs,
    aiRefineEnabled,
    formulationLockedRowsSummary,
    selectedFormulationTableVersion,
    getFormulationWithLockStatusFn,
    extractGroupMessageIds,
    shouldScrollToBottom,
    createdSessionId: readonly(createdSessionId),
    userTaskTitle,
    isFullScreenFocusMode,

    // Computed
    citationDocList,
    messages,
    messagesForDisplay,
    currentIteration,
    isCompleted,
    extractTableResult,
    extractSummaryResult,
    searchSummaryResult,
    eurekaReports,

    // Methods
    getConversation,
    createConversation,
    createConversationFromLanding,
    continueConversation,
    stopConversation,
    setSelectedTool,
    setSelectedExtractGroup,
    openEurekaReport,
    getEurekaReportForMessage,
    clearSelectedTool,
    setPendingUserMessage,
    clearPendingUserMessage,
    handleExampleClick,
    fetchToolResult,
    fetchAllSearchSources,
    getSourcesByDocIdForIteration,
    getIterationSourceCount,
    openSourcesPanel,
    scrollToLastMessage,
    enterFullScreenMode,
    exitFullScreenMode,
    resetConversation,
    buildAiRefineRequest,
    clearToolResultCache,
  };
});
